import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import EarnixLogo, { EarnixWordmark } from "./EarnixLogo";
import axios from "axios";
import useIsMobile from "../hooks/useIsMobile";

import API from "../config.js";
const G   = "#FFB800";
const G2  = "#FFD700";
const GD  = "#C8860A";
const C   = "#00C2FF";
const GRAD = `linear-gradient(135deg, ${G2}, ${G}, ${GD})`;

export default function Navbar() {
  const location = useLocation();
  const token    = localStorage.getItem("token");
  const isMobile = useIsMobile();

  const [menuOpen,   setMenuOpen]   = useState(false);
  const [notifOpen,  setNotifOpen]  = useState(false);
  const [notifs,     setNotifs]     = useState([]);
  const [unread,     setUnread]     = useState(0);
  const notifRef = useRef(null);


  const links = [
    { path: "/",             label: "Home"         },
    { path: "/dashboard",    label: "Dashboard"    },
    { path: "/invest",       label: "Invest"       },
    { path: "/wallet",       label: "Wallet"       },
    { path: "/transactions", label: "Transactions" },
    { path: "/downline",     label: "Network"      },
  ];

  // Fetch notifications (use notices as notifications)
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/notices`, { headers: { Authorization: "Bearer " + token } })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        // Add recent transactions as notifications too
        axios.get(`${API}/api/transactions`, { headers: { Authorization: "Bearer " + token } })
          .then(txRes => {
            const txns = Array.isArray(txRes.data) ? txRes.data : txRes.data?.transactions || [];
            const txNotifs = txns.slice(0, 5).map(t => ({
              _id: t._id,
              title: t.type === "deposit"        ? "Deposit Confirmed"
                   : t.type === "invest"          ? "Investment Active"
                   : t.type === "invest_withdraw" ? "Capital Withdrawn"
                   : t.type === "interest"        ? "Daily Interest"
                   : t.type === "referral_bonus"  ? "Referral Bonus"
                   : t.type === "level_income"    ? "Level Income"
                   : t.type === "withdraw"        ? "Withdrawal Update"
                   : t.type === "manual_credit"   ? "Admin Credit"
                   : t.type === "manual_debit"    ? "Admin Debit"
                   : "Transaction Update",
              message: `${t.note || t.type} — ${(Number(t.amount)||0).toFixed(2)} USDT`,
              type: t.type === "deposit" || t.type === "invest" || t.type === "interest" || t.type === "referral_bonus" || t.type === "level_income" ? "success" : "info",
              date: t.date,
            }));
            const all = [...data.map(n => ({ ...n, isNotice: true })), ...txNotifs];
            setNotifs(all.slice(0, 8));
            setUnread(all.length > 0 ? Math.min(all.length, 5) : 0);
          }).catch(() => {
            setNotifs(data);
            setUnread(data.length);
          });
      })
      .catch(() => {});
  }, [token]);

  // Close notif panel on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const logout = () => { localStorage.removeItem("token"); window.location.href = "/"; };

  const notifColors = { info: G, success: "#00e87a", warning: "#ffaa00", danger: "#ff6b6b" };

  return (
    <nav style={s.nav}>
      <div style={s.topBar} />
      <div style={s.inner}>

        {/* Brand */}
        <Link to="/" style={s.brand}>
          <EarnixLogo size={40} />
          <EarnixWordmark size={1} />
        </Link>

        {/* Desktop links */}
        <div style={{ ...s.links, display: isMobile ? "none" : "flex" }}>
          {token ? (
            <>
              {links.map(l => {
                const active = location.pathname === l.path;
                return (
                  <Link key={l.path} to={l.path}
                    style={{ ...s.link, ...(active ? s.linkActive : {}) }}>
                    {l.label}
                    {active && <span style={s.activeLine} />}
                  </Link>
                );
              })}

              {/* Notification Bell */}
              <div style={s.notifWrap} ref={notifRef}>
                <button style={s.bellBtn} onClick={() => { setNotifOpen(o => !o); setUnread(0); }}>
                  🔔
                  {unread > 0 && <span style={s.badge}>{unread}</span>}
                </button>

                {notifOpen && (
                  <div style={s.notifPanel}>
                    <div style={s.notifHeader}>
                      <span style={s.notifTitle}>Notifications</span>
                      <span style={s.notifCount}>{notifs.length} total</span>
                    </div>
                    <div style={s.notifList}>
                      {notifs.length === 0 ? (
                        <div style={s.notifEmpty}>No notifications yet.</div>
                      ) : notifs.map((n, i) => (
                        <div key={i} style={{ ...s.notifItem, borderLeft: `2px solid ${notifColors[n.type] || G}` }}>
                          <div style={{ ...s.notifItemTitle, color: notifColors[n.type] || G }}>{n.title}</div>
                          <div style={s.notifItemMsg}>{n.message}</div>
                          {n.date && <div style={s.notifItemDate}>{new Date(n.date).toLocaleDateString("en-IN", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</div>}
                        </div>
                      ))}
                    </div>
                    <Link to="/transactions" style={s.notifFooter} onClick={() => setNotifOpen(false)}>
                      View all transactions →
                    </Link>
                  </div>
                )}
              </div>

              {/* Profile */}
              <Link to="/profile" style={{ ...s.link, ...(location.pathname === "/profile" ? s.linkActive : {}) }}>
                👤
              </Link>

              <button onClick={logout} style={s.logoutBtn}>⏻ Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" style={s.signInBtn}>Sign In</Link>
              <Link to="/login" style={s.getStartedBtn}>Get Started</Link>
            </>
          )}
        </div>

        {/* Hamburger */}
        <button style={{ ...s.burger, display: isMobile ? "flex" : "none" }} onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
          <span style={{ ...s.bar, transform: menuOpen ? "rotate(45deg) translate(5px,5px)" : "none", transition:"transform 0.2s" }} />
          <span style={{ ...s.bar, opacity: menuOpen ? 0 : 1, transition:"opacity 0.2s" }} />
          <span style={{ ...s.bar, transform: menuOpen ? "rotate(-45deg) translate(5px,-5px)" : "none", transition:"transform 0.2s" }} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={s.mobile}>
          {token ? (
            <>
              {links.map(l => (
                <Link key={l.path} to={l.path} style={{ ...s.mobileLink, color: location.pathname === l.path ? G2 : "rgba(160,180,220,0.7)" }}
                  onClick={() => setMenuOpen(false)}>{l.label}</Link>
              ))}
              <Link to="/profile" style={s.mobileLink} onClick={() => setMenuOpen(false)}>Profile</Link>
              <button onClick={logout} style={s.mobileLogout}>⏻ Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" style={s.mobileLink} onClick={() => setMenuOpen(false)}>Sign In</Link>
              <Link to="/login" style={{ ...s.mobileLink, color: G }} onClick={() => setMenuOpen(false)}>Get Started</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

const s = {
  nav: {
    position: "sticky", top: 0, zIndex: 1000,
    background: "rgba(6,13,26,0.95)",
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    borderBottom: "1px solid rgba(255,184,0,0.1)",
  },
  topBar: { height: 2, background: `linear-gradient(90deg, transparent 0%, ${G2} 30%, ${G} 50%, ${C} 70%, transparent 100%)` },
  inner: {
    maxWidth: 1280, margin: "0 auto",
    padding: "0 24px", height: 64,
    display: "flex", alignItems: "center",
    justifyContent: "space-between",
  },
  brand: { display: "flex", alignItems: "center", gap: 10, textDecoration: "none", flexShrink: 0, lineHeight: 1 },
  links: { display: "flex", alignItems: "center", gap: 2, "@media(max-width:768px)": { display: "none" } },
  link: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 13,
    color: "rgba(160,180,220,0.65)", padding: "8px 12px", borderRadius: 8,
    position: "relative", letterSpacing: 0.4, textDecoration: "none", transition: "color 0.2s",
  },
  linkActive: { color: G2 },
  activeLine: {
    position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
    width: "60%", height: 2, borderRadius: 2,
    background: `linear-gradient(90deg, ${G2}, ${G})`,
    boxShadow: `0 0 8px ${G}`, display: "block",
  },

  /* Notification */
  notifWrap: { position: "relative" },
  bellBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 18, padding: "6px 10px", borderRadius: 8,
    position: "relative", color: "rgba(160,180,220,0.6)",
    transition: "color 0.2s",
  },
  badge: {
    position: "absolute", top: 2, right: 2,
    background: "#ff6b6b", color: "#fff",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 700, fontSize: 9,
    borderRadius: "50%", width: 16, height: 16,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  notifPanel: {
    position: "absolute", top: "calc(100% + 10px)", right: 0,
    width: 320, maxHeight: 420,
    background: "rgba(6,13,26,0.98)", border: "1px solid rgba(255,184,0,0.2)",
    borderRadius: 12, overflow: "hidden",
    boxShadow: "0 16px 50px rgba(0,0,0,0.6)",
    zIndex: 999,
  },
  notifHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "14px 16px", borderBottom: "1px solid rgba(255,184,0,0.08)",
  },
  notifTitle: { fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 12, color: G2, letterSpacing: 1 },
  notifCount: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 11, color: "rgba(160,180,220,0.35)" },
  notifList: { maxHeight: 320, overflowY: "auto" },
  notifEmpty: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 13, color: "rgba(160,180,220,0.3)", padding: "24px", textAlign: "center" },
  notifItem: {
    padding: "12px 16px", borderBottom: "1px solid rgba(255,184,0,0.04)",
    marginLeft: 4, transition: "background 0.15s",
  },
  notifItemTitle: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 700, fontSize: 12, marginBottom: 3 },
  notifItemMsg: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 12, color: "rgba(160,180,220,0.55)", lineHeight: 1.4 },
  notifItemDate: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 10, color: "rgba(160,180,220,0.3)", marginTop: 4 },
  notifFooter: {
    display: "block", padding: "12px 16px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 12, color: G,
    textDecoration: "none", textAlign: "center",
    borderTop: "1px solid rgba(255,184,0,0.08)",
  },

  signInBtn: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 13,
    color: "rgba(255,184,0,0.85)", border: "1px solid rgba(255,184,0,0.25)",
    borderRadius: 8, padding: "8px 20px", textDecoration: "none",
  },
  getStartedBtn: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 700, fontSize: 13,
    color: "#060D1A", background: GRAD,
    borderRadius: 8, padding: "8px 20px",
    textDecoration: "none", marginLeft: 6,
    boxShadow: "0 0 20px rgba(255,184,0,0.3)",
  },
  logoutBtn: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 12,
    color: "#ff6b8a", background: "rgba(255,60,87,0.07)",
    border: "1px solid rgba(255,60,87,0.18)",
    borderRadius: 8, padding: "8px 14px", cursor: "pointer", marginLeft: 4,
  },

  /* Hamburger */
  burger: {
    display: "none", flexDirection: "column", background: "none", border: "none",
    cursor: "pointer", gap: 5, padding: 6, zIndex: 1001,
    "@media(max-width:768px)": { display: "flex" },
  },
  bar: { display: "block", width: 22, height: 2, background: G, borderRadius: 2 },

  /* Mobile menu */
  mobile: {
    display: "flex", flexDirection: "column",
    padding: "12px 24px 24px",
    borderTop: "1px solid rgba(255,184,0,0.07)",
    gap: 2, background: "rgba(4,8,16,0.99)",
  },
  mobileLink: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600,
    color: "rgba(160,180,220,0.7)", padding: "12px 8px", fontSize: 14,
    textDecoration: "none", borderBottom: "1px solid rgba(255,184,0,0.05)",
    transition: "color 0.2s",
  },
  mobileLogout: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", marginTop: 12, color: "#ff6b8a",
    background: "rgba(255,60,87,0.07)", border: "1px solid rgba(255,60,87,0.2)",
    borderRadius: 8, padding: "12px 16px", cursor: "pointer", fontSize: 14,
    textAlign: "center",
  },
};


